import { useEffect, useState, type ReactNode } from "react";
import { useAuth } from "./AuthProvider";
import { tenantAccessOutcome, type TenantAccess } from "./access";
import { joinUrlPath, runtimeUrls, validateReturnTo } from "../config/domains";
import { ExternalRedirect } from "../components/ExternalRedirect";
import { AccessErrorPage } from "../pages/AccessPages";
import { supabase } from "../lib/supabase";

export function TenantGate({ slug, children }: { slug: string; children: ReactNode }) {
  const { user, loading, resolveTenantAccess } = useAuth();
  const [access, setAccess] = useState<TenantAccess | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    const revalidateAfterHistoryRestore = (event: PageTransitionEvent) => { if (event.persisted) window.location.reload(); };
    window.addEventListener("pageshow", revalidateAfterHistoryRestore);
    return () => window.removeEventListener("pageshow", revalidateAfterHistoryRestore);
  }, []);

  useEffect(() => {
    if (!user) return;
    let active = true;
    setAccess(null); setFailed(false);
    void resolveTenantAccess(slug).then((result) => {
      if (!active) return;
      setAccess(result);
      if (result.allowed && supabase) void supabase.rpc("record_workspace_access", { p_tenant_slug: slug });
    }).catch(() => { if (active) setFailed(true); });
    return () => { active = false; };
  }, [resolveTenantAccess, slug, user]);

  if (loading) return <div className="app-loader"><span/><strong>POSITRONICO</strong></div>;
  if (!user) {
    const returnTo = validateReturnTo(window.location.href);
    const login = new URL(joinUrlPath(runtimeUrls().appUrl, "/login")); login.searchParams.set("returnTo", returnTo);
    return <ExternalRedirect to={login.toString()}/>;
  }
  if (failed) return <AccessErrorPage code="403" title="No pudimos verificar tu acceso" message="Intenta de nuevo o contacta a POSITRONICO si el problema continúa."/>;
  if (!access) return <div className="app-loader"><span/><strong>Verificando acceso</strong></div>;
  const outcome = tenantAccessOutcome(access);
  if (outcome === "not-found") return <AccessErrorPage code="404" title="Workspace no encontrado" message="Revisa la dirección del portal."/>;
  if (outcome === "organization-inactive") return <AccessErrorPage code="INACTIVO" title="Workspace inactivo" message="La organización no está disponible en este momento."/>;
  if (outcome === "membership-invited") return <ExternalRedirect to={joinUrlPath(runtimeUrls().appUrl, "/set-password")}/>;
  if (outcome === "membership-disabled") return <AccessErrorPage code="BLOQUEADO" title="Acceso deshabilitado" message="Tu acceso a esta organización fue deshabilitado."/>;
  if (outcome === "forbidden") return <AccessErrorPage code="403" title="Sin acceso a este workspace" message="Tu cuenta no tiene una membresía activa para esta organización."/>;
  return children;
}
